import React, { useEffect, useState } from 'react';
import { getSubscriptionStatus, getCustomerPortal, cancelSubscription, SubscriptionStatus } from '../../services/subscriptionService';
import { toast } from 'react-toastify';
import './Pricing.css'; // Reuse basic layout styles

const SubscriptionManagementPage: React.FC = () => {
    const [subscription, setSubscription] = useState<SubscriptionStatus | null>(null);
    const [loading, setLoading] = useState(true);
    const [processing, setProcessing] = useState(false);

    useEffect(() => {
        fetchSubscription();
    }, []);

    const fetchSubscription = async () => {
        try {
            const data = await getSubscriptionStatus();
            setSubscription(data);
        } catch (error) {
            console.error('Error fetching subscription:', error);
            toast.error('Failed to load subscription details');
        } finally {
            setLoading(false);
        }
    };

    const handleOpenPortal = async () => {
        setProcessing(true);
        try {
            const { url } = await getCustomerPortal();
            if (url) {
                window.location.href = url;
            }
        } catch (error) {
            console.error('Error opening portal:', error);
            toast.error('Failed to load billing portal');
        } finally {
            setProcessing(false);
        }
    };

    const handleCancel = async () => {
        if (!window.confirm('Are you sure you want to cancel your subscription? You will lose access to Pro features at the end of the current period.')) {
            return;
        }

        setProcessing(true);
        try {
            await cancelSubscription();
            toast.success('Subscription canceled successfully');
            fetchSubscription(); // Refresh status
        } catch (error) {
            console.error('Error canceling subscription:', error);
            toast.error('Failed to cancel subscription');
        } finally {
            setProcessing(false);
        }
    };

    if (loading) {
        return <div className="pricing-container"><div className="loading-spinner">Loading...</div></div>;
    }

    if (!subscription || !subscription.hasSubscription) {
        return (
            <div className="pricing-container">
                <div className="pricing-card" style={{ maxWidth: '600px', margin: '0 auto', textAlign: 'center' }}>
                    <h2>No Active Subscription</h2>
                    <p>You are currently on the Free plan.</p>
                    <a href="/pricing" className="cta-button" style={{ display: 'inline-block', marginTop: '20px', textDecoration: 'none' }}>
                        View Plans
                    </a>
                </div>
            </div>
        );
    }

    return (
        <div className="pricing-container">
            <div className="pricing-header">
                <h1>Subscription Management</h1>
            </div>

            <div className="pricing-card" style={{ maxWidth: '700px', margin: '0 auto', width: '100%', textAlign: 'left' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                    <h2>{subscription.planName || 'Pro Plan'}</h2>
                    <span className={`status-badge ${subscription.status}`} style={{
                        padding: '6px 12px',
                        borderRadius: '20px',
                        backgroundColor: subscription.status === 'active' ? '#dcfce7' : '#fee2e2',
                        color: subscription.status === 'active' ? '#166534' : '#991b1b',
                        fontWeight: 'bold',
                        textTransform: 'capitalize'
                    }}>
                        {subscription.status}
                    </span>
                </div>

                <div className="subscription-details" style={{ marginBottom: '30px' }}>
                    <div className="detail-row" style={{ padding: '10px 0', borderBottom: '1px solid #eee' }}>
                        <strong>Current Period:</strong>
                        <span style={{ float: 'right' }}>
                            {new Date(subscription.currentPeriodStart!).toLocaleDateString()} - {new Date(subscription.currentPeriodEnd!).toLocaleDateString()}
                        </span>
                    </div>
                    {subscription.cancelAtPeriodEnd && (
                        <div className="detail-row" style={{ padding: '10px 0', borderBottom: '1px solid #eee', color: '#eab308' }}>
                            <strong>Status:</strong>
                            <span style={{ float: 'right' }}>Matches expire on {new Date(subscription.currentPeriodEnd!).toLocaleDateString()}</span>
                        </div>
                    )}
                </div>

                <div className="actions" style={{ display: 'flex', gap: '15px', flexDirection: 'column' }}>
                    <button
                        className="cta-button"
                        onClick={handleOpenPortal}
                        disabled={processing}
                    >
                        Manage Billing / Update Card
                    </button>

                    {!subscription.cancelAtPeriodEnd && subscription.status === 'active' && (
                        <button
                            className="cancel-button"
                            onClick={handleCancel}
                            disabled={processing}
                            style={{
                                background: 'transparent',
                                border: '1px solid #ef4444',
                                color: '#ef4444',
                                padding: '12px',
                                borderRadius: '50px',
                                cursor: 'pointer',
                                fontWeight: 600
                            }}
                        >
                            Cancel Subscription
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
};

export default SubscriptionManagementPage;
